import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import { Loading, useMedplum } from "@medplum/react";

import { Accordion, Group, Paper, Stack, Text, Title } from "@mantine/core";

import { IconNotes } from "@tabler/icons-react";

import { TextWrapper } from "@/components/TextWrapper";
import { WordsDiff } from "@/components/WordsDiff";
import {
  clearNewbornPostpartumDiffCache,
  getNewbornPostpartumPhysicalExamDiffHistory,
  NewbornPostpartumPhysicalExamAnswer,
  NewbornPostpartumPhysicalExamDiffResult,
} from "@/physical-exams/services/newborn-postpartum-physical-exam-diff";

// Helper function to extract section data from processed answers
function getSectionDataFromAnswers(answers: NewbornPostpartumPhysicalExamAnswer[]) {
  const sections: Record<
    string,
    {
      normal: boolean;
      notNormal: boolean;
      declined: boolean;
      normalDetails: string[];
      notNormalDetails: string[];
      notes: string;
    }
  > = {};

  for (const answer of answers) {
    if (answer?.type === "newborn-section") {
      sections[answer.sectionName] = {
        normal: answer.normal,
        notNormal: answer.notNormal,
        declined: answer.declined,
        normalDetails: answer.normalDetails,
        notNormalDetails: answer.notNormalDetails,
        notes: answer.notes,
      };
    }
  }

  return sections;
}

const booleanToYesOrNo = (value: boolean): string => {
  return value ? "Yes" : "No";
};

const getSectionStatus = (section: {
  normal: boolean;
  notNormal: boolean;
  declined: boolean;
}): string => {
  if (section.declined) return "Declined";
  if (section.normal) return "Normal";
  if (section.notNormal) return "Not Normal";
  return "Not answered";
};

const NewbornPostpartumPhysicalExamsDiff = ({
  currentVersionIndex,
  versionId,
}: {
  currentVersionIndex: number;
  versionId?: string;
}) => {
  const { encounterId } = useParams<{ encounterId: string }>();
  const medplum = useMedplum();

  const [isFetching, setIsFetching] = useState(true);
  const [histories, setHistories] = useState<NewbornPostpartumPhysicalExamDiffResult>({});

  useEffect(() => {
    let mounted = true;
    (async () => {
      setIsFetching(true);
      try {
        // Get the encounter to pass to the service
        const encounter = await medplum.readResource("Encounter", encounterId!);

        // Use the newborn-specific service that returns the correct format
        const histories = await getNewbornPostpartumPhysicalExamDiffHistory(
          medplum,
          encounter,
          versionId,
          currentVersionIndex,
        );

        if (mounted) {
          setHistories(histories);
          setIsFetching(false);
        }
      } catch (error) {
        console.error("Error fetching newborn postpartum physical exam diff:", error);
        if (mounted) {
          setIsFetching(false);
        }
      }
    })();
    return () => {
      mounted = false;
    };
  }, [currentVersionIndex, encounterId, medplum, versionId]);

  // Clear cache when component unmounts to prevent memory leaks
  useEffect(() => {
    return () => {
      if (encounterId) {
        clearNewbornPostpartumDiffCache(encounterId);
      }
    };
  }, [encounterId]);

  if (isFetching) return <Loading />;

  // Check if there's any physical exam data in either current or previous versions
  const hasAnyData = Object.values(histories).some((history) => {
    // History is now zipped data: [[current, previous], [current, previous], ...]
    return history.length > 0;
  });

  if (!hasAnyData) {
    return (
      <Paper p="lg" withBorder>
        <Title mb="lg" order={2}>
          Physical Exams
        </Title>
        <TextWrapper py="lg" c="gray.6" ta="center">
          No physical exam data has been recorded for this encounter.
        </TextWrapper>
      </Paper>
    );
  }

  return (
    <Paper p="lg" withBorder>
      <Title mb="lg" order={2}>
        Physical Exams
      </Title>
      <Accordion multiple>
        {Object.entries(histories).map(([title, history]) => {
          // History is now zipped data: [[current, previous], [current, previous], ...]
          // We need to flatten it to get all current and previous answers
          const allCurrentAnswers: NewbornPostpartumPhysicalExamAnswer[] = [];
          const allPreviousAnswers: NewbornPostpartumPhysicalExamAnswer[] = [];

          history.forEach(([current, previous]) => {
            if (current) allCurrentAnswers.push(current);
            if (previous) allPreviousAnswers.push(previous);
          });

          if (allCurrentAnswers.length === 0 && allPreviousAnswers.length === 0) return null;

          const currentSections = getSectionDataFromAnswers(allCurrentAnswers);
          const previousSections = getSectionDataFromAnswers(allPreviousAnswers);

          // Get all unique section names from both versions
          const allSectionNames = new Set([
            ...Object.keys(currentSections),
            ...Object.keys(previousSections),
          ]);

          return (
            <Accordion.Item key={title} value={title}>
              <Accordion.Control>
                <Group>
                  <IconNotes size={24} color="#a0a0a0" style={{ marginRight: 8 }} />
                  <TextWrapper c="charcoal.0" variant="bold">
                    {title}
                  </TextWrapper>
                </Group>
              </Accordion.Control>
              <Accordion.Panel>
                <Stack gap="md">
                  {Array.from(allSectionNames).map((sectionName) => {
                    const currentSection = currentSections[sectionName];
                    const previousSection = previousSections[sectionName];

                    // If no data in either version, skip
                    if (!currentSection && !previousSection) return null;

                    return (
                      <Paper p="md" withBorder key={sectionName}>
                        <Stack mb="lg">
                          <Title order={4}>
                            {sectionName
                              .replace(/([A-Z])/g, " $1")
                              .replace(/^./, (str) => str.toUpperCase())
                              .trim()}
                          </Title>
                        </Stack>
                        <Group grow align="stretch">
                          <Paper p="md" withBorder>
                            <Stack>
                              <Title mb="lg" order={5} c="gray.6">
                                Previous
                              </Title>
                              {!previousSection ? (
                                <Text c="gray.6">No data available</Text>
                              ) : (
                                <Stack gap="md">
                                  <Stack gap="0">
                                    <TextWrapper fw={600}>Status</TextWrapper>
                                    <WordsDiff
                                      showRemoved
                                      previous={getSectionStatus(previousSection)}
                                      current={getSectionStatus(currentSection || {})}
                                    />
                                  </Stack>
                                  <Stack gap="0">
                                    <TextWrapper fw={600}>Normal</TextWrapper>
                                    <WordsDiff
                                      showRemoved
                                      previous={booleanToYesOrNo(previousSection.normal)}
                                      current={booleanToYesOrNo(currentSection?.normal || false)}
                                    />
                                  </Stack>
                                  <Stack gap="0">
                                    <TextWrapper fw={600}>Not Normal</TextWrapper>
                                    <WordsDiff
                                      showRemoved
                                      previous={booleanToYesOrNo(previousSection.notNormal)}
                                      current={booleanToYesOrNo(currentSection?.notNormal || false)}
                                    />
                                  </Stack>
                                  <Stack gap="0">
                                    <TextWrapper fw={600}>Declined</TextWrapper>
                                    <WordsDiff
                                      showRemoved
                                      previous={booleanToYesOrNo(previousSection.declined)}
                                      current={booleanToYesOrNo(currentSection?.declined || false)}
                                    />
                                  </Stack>
                                  {previousSection.normalDetails.length > 0 && (
                                    <Stack gap="0">
                                      <TextWrapper fw={600}>Normal Details</TextWrapper>
                                      <WordsDiff
                                        showRemoved
                                        previous={previousSection.normalDetails.join(", ")}
                                        current={(currentSection?.normalDetails || []).join(", ")}
                                      />
                                    </Stack>
                                  )}
                                  {previousSection.notNormalDetails.length > 0 && (
                                    <Stack gap="0">
                                      <TextWrapper fw={600}>Not Normal Details</TextWrapper>
                                      <WordsDiff
                                        showRemoved
                                        previous={previousSection.notNormalDetails.join(", ")}
                                        current={(currentSection?.notNormalDetails || []).join(
                                          ", ",
                                        )}
                                      />
                                    </Stack>
                                  )}
                                  {(previousSection.notes || currentSection?.notes) && (
                                    <Stack gap="0">
                                      <TextWrapper fw={600}>Notes</TextWrapper>
                                      <WordsDiff
                                        showRemoved
                                        previous={previousSection.notes}
                                        current={currentSection?.notes || ""}
                                      />
                                    </Stack>
                                  )}
                                </Stack>
                              )}
                            </Stack>
                          </Paper>
                          <Paper p="md" withBorder>
                            <Stack>
                              <Title mb="lg" order={5} c="gray.6">
                                Amended
                              </Title>
                              {!currentSection ? (
                                <Text c="gray.6">No data available</Text>
                              ) : (
                                <Stack gap="md">
                                  <Stack gap="0">
                                    <TextWrapper fw={600}>Status</TextWrapper>
                                    <WordsDiff
                                      current={getSectionStatus(currentSection)}
                                      previous={getSectionStatus(previousSection || {})}
                                    />
                                  </Stack>
                                  <Stack gap="0">
                                    <TextWrapper fw={600}>Normal</TextWrapper>
                                    <WordsDiff
                                      current={booleanToYesOrNo(currentSection.normal)}
                                      previous={booleanToYesOrNo(previousSection?.normal || false)}
                                    />
                                  </Stack>
                                  <Stack gap="0">
                                    <TextWrapper fw={600}>Not Normal</TextWrapper>
                                    <WordsDiff
                                      current={booleanToYesOrNo(currentSection.notNormal)}
                                      previous={booleanToYesOrNo(
                                        previousSection?.notNormal || false,
                                      )}
                                    />
                                  </Stack>
                                  <Stack gap="0">
                                    <TextWrapper fw={600}>Declined</TextWrapper>
                                    <WordsDiff
                                      current={booleanToYesOrNo(currentSection.declined)}
                                      previous={booleanToYesOrNo(
                                        previousSection?.declined || false,
                                      )}
                                    />
                                  </Stack>
                                  {currentSection.normalDetails.length > 0 && (
                                    <Stack gap="0">
                                      <TextWrapper fw={600}>Normal Details</TextWrapper>
                                      <WordsDiff
                                        current={currentSection.normalDetails.join(", ")}
                                        previous={(previousSection?.normalDetails || []).join(", ")}
                                      />
                                    </Stack>
                                  )}
                                  {currentSection.notNormalDetails.length > 0 && (
                                    <Stack gap="0">
                                      <TextWrapper fw={600}>Not Normal Details</TextWrapper>
                                      <WordsDiff
                                        current={currentSection.notNormalDetails.join(", ")}
                                        previous={(previousSection?.notNormalDetails || []).join(
                                          ", ",
                                        )}
                                      />
                                    </Stack>
                                  )}
                                  {(previousSection?.notes || currentSection.notes) && (
                                    <Stack gap="0">
                                      <TextWrapper fw={600}>Notes</TextWrapper>
                                      <WordsDiff
                                        current={currentSection.notes}
                                        previous={previousSection?.notes || ""}
                                      />
                                    </Stack>
                                  )}
                                </Stack>
                              )}
                            </Stack>
                          </Paper>
                        </Group>
                      </Paper>
                    );
                  })}
                </Stack>
              </Accordion.Panel>
            </Accordion.Item>
          );
        })}
      </Accordion>
    </Paper>
  );
};

export default NewbornPostpartumPhysicalExamsDiff;
